import { useEffect, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'

import { clearWriteTrouble, subscribeWriteTrouble, writeTrouble } from '~/api/attempt'

import s from './WriteTrouble.module.css'

/**
 * The one place a failed write is said. Mounted once, above every route, because the writes
 * come from eight surfaces and a reader on any of them deserves the same sentence.
 *
 * Drawn inside the open sheet when there is one. The event sheet and the avatar editor open
 * with `showModal()`, which puts the dialog in the top layer and makes
 * everything outside it inert — no `z-index` reaches the top layer, and a popover shown
 * over a modal is inert too (checked in Chrome: the hit test lands on the dialog and focus
 * will not take). A strip at the body was therefore dimmed behind the backdrop with a
 * Dismiss that could not be pressed, on the very surface the original 400 was on. A
 * descendant of the dialog is neither dimmed nor inert, so the strip is portalled into
 * whichever modal is open and back to the body when none is.
 *
 * Nothing while there is nothing to say. No timer: a reader who looks up ten seconds later
 * should still find out, so it stays until dismissed or until a later write succeeds.
 */
export function WriteTrouble() {
  const trouble = useSyncExternalStore(subscribeWriteTrouble, writeTrouble, () => null)
  const host = useModalHost()
  if (trouble === null) return null
  return createPortal(
    <div className={s.strip} role="alert">
      <span className={s.say}>{trouble}</span>
      <button type="button" className={s.dismiss} onClick={clearWriteTrouble}>
        Dismiss
      </button>
    </div>,
    host ?? document.body,
  )
}

/** The open `<dialog>` the strip must live inside to be reachable, or `null` for the body. */
function openModal(): HTMLDialogElement | null {
  const open = document.querySelectorAll<HTMLDialogElement>('dialog[open]')
  // The last one in the document. The site opens one modal at a time today — the event
  // sheet, or the avatar editor on the profile — so this is a tie-break for a case it does
  // not have; if two ever stack, the one rendered later is the one on top.
  return open[open.length - 1] ?? null
}

/**
 * Which modal is open, kept current by watching `open` come and go on any dialog — that
 * attribute is what `showModal()` and `close()` toggle, and a dialog mounting already open
 * is a child-list change. Nothing is set unless the answer changed, so the watch is cheap
 * however often the page renders.
 */
function useModalHost(): HTMLDialogElement | null {
  const [host, setHost] = useState<HTMLDialogElement | null>(() => openModal())
  useEffect(() => {
    const look = () => setHost(was => (was === openModal() ? was : openModal()))
    look()
    const watch = new MutationObserver(look)
    watch.observe(document.body, { attributes: true, attributeFilter: ['open'], childList: true, subtree: true })
    return () => watch.disconnect()
  }, [])
  return host
}
